/**
 * ReportsPanel — Salesperson Performance report.
 *
 * ONE row per salesperson within the selected date range, showing
 * Total Quotations, Total Value, Closed / Pending / Lost counts +
 * values, and Conversion %.
 *
 * Numbers come from GET /api/admin/sales-stats — the SAME endpoint
 * the Analytics tab reads for its "Salesperson Breakdown" table —
 * so the two screens can never show different numbers for the same
 * period. Query path: dedupe_quotations → collapse_comparison_pairs
 * → aggregate. Missing/null order_status counts as pending
 * (consistent with the rest of the app).
 *
 * Click a Pending / Lost count to expand a per-quotation drill-down
 * row (fetches GET /api/admin/salesperson-drilldown which reuses
 * the identical dedupe+collapse pipeline).
 *
 * Admin/manager only — enforced by /admin/sales-stats +
 * /admin/salesperson-drilldown both using get_admin_user.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Loader2, Users, ChevronDown, ChevronRight, IndianRupee, Download } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/store';

const fmtINR = (n) => {
  const v = Math.round(Number(n) || 0);
  return `₹${v.toLocaleString('en-IN')}`;
};
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const isoToday = () => new Date().toISOString().slice(0, 10);

export default function ReportsPanel() {
  const [rangePreset, setRangePreset] = useState('month');
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [endDate, setEndDate] = useState(isoToday());
  const [salesperson, setSalesperson] = useState('all');
  const [salespeople, setSalespeople] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});  // key: `${uid}:pending|lost` → { loading, rows }
  // Report download: dropdown chooses which of 4 report
  // types the "Download PDF" button will render.
  const [reportType, setReportType] = useState('pending');
  const [downloading, setDownloading] = useState(false);

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const params = { type: reportType, salesperson };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await api.downloadReportPdf(params);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      const slug = { pending: 'Pending_Quotations', lost: 'Lost_Quotations',
                     complaints: 'Complaints', leads: 'Leads' }[reportType];
      a.href = url;
      a.download = `HEDA_${slug}_Report_${today}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (err) {
      // err.response.data is a Blob when responseType is 'blob' — parse it.
      let msg = 'Failed to download report';
      try {
        const blob = err.response?.data;
        if (blob && typeof blob.text === 'function') {
          const raw = await blob.text();
          try { msg = JSON.parse(raw).detail || msg; } catch { msg = raw || msg; }
        } else if (err.response?.data?.detail) {
          msg = err.response.data.detail;
        }
      } catch { /* ignore */ }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const applyPreset = (preset) => {
    setRangePreset(preset);
    if (preset === 'all') { setStartDate(''); setEndDate(''); }
    else if (preset === 'week') {
      const now = new Date();
      const dow = (now.getDay() + 6) % 7;
      const monday = new Date(now); monday.setDate(now.getDate() - dow);
      setStartDate(monday.toISOString().slice(0, 10));
      setEndDate(isoToday());
    } else if (preset === 'month') {
      const now = new Date();
      setStartDate(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
      setEndDate(isoToday());
    } else if (preset === '30d') {
      setStartDate(isoDaysAgo(29)); setEndDate(isoToday());
    } else if (preset === 'custom' && !startDate && !endDate) {
      setStartDate(isoDaysAgo(29)); setEndDate(isoToday());
    }
  };

  // Fetch salespeople list once (same endpoint the digest tools already use).
  useEffect(() => {
    api.getSalespeople().then(r => setSalespeople(r.data?.salespeople || r.data || [])).catch(() => setSalespeople([]));
  }, []);

  // Fetch sales-stats whenever the range changes. Drill-down cache
  // reset so stale rows don't linger under a different range.
  const fetchRows = useCallback(() => {
    setLoading(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    api.getAdminAnalytics(params)
      .then(r => {
        // Fix: /admin/analytics returns `by_salesperson`
        // (aggregated closed/pending/lost counts + values, dedupe +
        // collapse already applied) — same array the Analytics tab's
        // Salesperson Breakdown table reads. Field names:
        //   name, staff_code, count, value,
        //   closed_count / pending_count / lost_count,
        //   closed_value / pending_value / lost_value.
        const bySales = r.data?.by_salesperson || [];
        setRows(bySales);
        // Analytics endpoint doesn't emit a `summary` block in the
        // same shape sales-stats does; derive a light one for the
        // hint line so numbers match what's rendered.
        const totalCount = bySales.reduce((s, x) => s + (x.count || 0), 0);
        const totalValue = bySales.reduce((s, x) => s + (x.value || 0), 0);
        setSummary({ total_count: totalCount, total_value: totalValue });
        setExpanded({});
      })
      .catch(() => { setRows([]); setSummary(null); })
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const visibleRows = salesperson === 'all'
    ? rows
    : rows.filter(r => r.user_id === salesperson);

  const toggleDrill = async (uid, status) => {
    const key = `${uid}:${status}`;
    if (expanded[key]) {
      setExpanded(prev => { const next = { ...prev }; delete next[key]; return next; });
      return;
    }
    setExpanded(prev => ({ ...prev, [key]: { loading: true, rows: [] } }));
    try {
      const params = { user_id: uid, status };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const r = await api.getSalespersonDrilldown(params);
      setExpanded(prev => ({ ...prev, [key]: { loading: false, rows: r.data?.quotations || [] } }));
    } catch (e) {
      setExpanded(prev => ({ ...prev, [key]: { loading: false, rows: [], error: true } }));
    }
  };

  return (
    <div className="space-y-4" data-testid="reports-panel">
      <Card className="border-[#E2E6D9]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg text-[#1A1C16] flex items-center gap-2">
            <Users className="w-5 h-5 text-[#556B2F]" />
            Salesperson Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:items-center" data-testid="reports-filter-bar">
            {/* 1. Report Type */}
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="w-full sm:w-52 h-9 bg-white border-[#E2E6D9]" data-testid="reports-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending" data-testid="reports-type-pending">Pending Quotations</SelectItem>
                <SelectItem value="lost" data-testid="reports-type-lost">Lost Quotations</SelectItem>
                <SelectItem value="complaints" data-testid="reports-type-complaints">Complaints</SelectItem>
                <SelectItem value="leads" data-testid="reports-type-leads">Leads</SelectItem>
              </SelectContent>
            </Select>

            {/* 2. Salesperson (existing, unchanged behaviour — just moved) */}
            <Select value={salesperson} onValueChange={setSalesperson}>
              <SelectTrigger className="w-full sm:w-52 h-9 bg-white border-[#E2E6D9]" data-testid="reports-salesperson-select">
                <SelectValue placeholder="All salespeople" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All salespeople</SelectItem>
                {salespeople.map(s => (
                  <SelectItem key={s.id || s.user_id} value={s.id || s.user_id}>
                    {s.name}{s.staff_code ? ` · ${s.staff_code}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* 3. Date Range — labels map onto applyPreset's existing keys. */}
            <Select value={rangePreset} onValueChange={applyPreset}>
              <SelectTrigger className="w-full sm:w-52 h-9 bg-white border-[#E2E6D9]" data-testid="reports-daterange-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all"    data-testid="reports-daterange-all">All Time</SelectItem>
                <SelectItem value="week"   data-testid="reports-daterange-week">This Week</SelectItem>
                <SelectItem value="month"  data-testid="reports-daterange-month">This Month</SelectItem>
                <SelectItem value="30d"    data-testid="reports-daterange-30d">Last 30 Days</SelectItem>
                <SelectItem value="custom" data-testid="reports-daterange-custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {/* 4. Download PDF (existing, unchanged behaviour — just moved) */}
            <Button
              type="button"
              onClick={handleDownloadReport}
              disabled={downloading}
              className="h-9 bg-[#556B2F] hover:bg-[#3F5120] text-white"
              data-testid="reports-download-pdf-btn"
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
              Download PDF
            </Button>
          </div>

          {/* Custom Range inputs — visible ONLY when 'custom' is selected.
              Uses the same startDate/endDate state as before; hidden (not
              disabled) for every other preset, and switching away leaves
              state intact. */}
          {rangePreset === 'custom' && (
            <div className="flex flex-col sm:flex-row gap-2 sm:items-end" data-testid="reports-custom-range-row">
              <div className="flex flex-col gap-1">
                <label className="text-[11px] text-[#5C6150]" htmlFor="reports-from-date">From</label>
                <Input
                  id="reports-from-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setRangePreset('custom'); }}
                  className="w-full sm:w-44 h-9 bg-white border-[#E2E6D9]"
                  data-testid="reports-from-date"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] text-[#5C6150]" htmlFor="reports-to-date">To</label>
                <Input
                  id="reports-to-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setRangePreset('custom'); }}
                  className="w-full sm:w-44 h-9 bg-white border-[#E2E6D9]"
                  data-testid="reports-to-date"
                />
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[#5C6150] py-4 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : visibleRows.length === 0 ? (
            <p className="text-sm text-[#8F9485] italic py-4 text-center">
              No quotation activity in the selected range.
            </p>
          ) : (
            <div className="border border-[#E2E6D9] rounded-md overflow-x-auto">
              <table className="w-full text-xs min-w-[820px]" data-testid="reports-salesperson-table">
                <thead className="bg-[#F2F4EF] text-[#5C6150]">
                  <tr>
                    <th className="text-left py-2 px-3">Salesperson</th>
                    <th className="text-right py-2 px-3">Total</th>
                    <th className="text-right py-2 px-3">Value</th>
                    <th className="text-right py-2 px-3">Closed</th>
                    <th className="text-right py-2 px-3">Pending</th>
                    <th className="text-right py-2 px-3">Lost</th>
                    <th className="text-right py-2 px-3">Conv. %</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(r => {
                    const uid = r.user_id;
                    const pKey = `${uid}:pending`;
                    const lKey = `${uid}:lost`;
                    return (
                      <React.Fragment key={uid}>
                        <tr className="border-t border-[#E2E6D9] hover:bg-[#F9FAF7]" data-testid={`reports-row-${uid}`}>
                          <td className="py-2 px-3 font-medium">
                            {r.name}
                            {r.staff_code && (
                              <Badge variant="outline" className="ml-2 text-[9px]">{r.staff_code}</Badge>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right font-mono-data">{r.count}</td>
                          <td className="py-2 px-3 text-right font-mono-data text-[#DAA520]">{fmtINR(r.value)}</td>
                          <td className="py-2 px-3 text-right font-mono-data">
                            <span className="text-[#556B2F] font-semibold">{r.closed_count}</span>
                            <span className="text-[#8F9485] text-[10px] block">{fmtINR(r.closed_value)}</span>
                          </td>
                          <td className="py-2 px-3 text-right font-mono-data">
                            <button
                              type="button"
                              onClick={() => toggleDrill(uid, 'pending')}
                              className="inline-flex items-center gap-1 hover:underline text-[#8B6914]"
                              data-testid={`reports-drill-pending-${uid}`}
                            >
                              {expanded[pKey] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              <span className="font-semibold">{r.pending_count}</span>
                            </button>
                            <span className="text-[#8F9485] text-[10px] block">{fmtINR(r.pending_value)}</span>
                          </td>
                          <td className="py-2 px-3 text-right font-mono-data">
                            <button
                              type="button"
                              onClick={() => toggleDrill(uid, 'lost')}
                              className="inline-flex items-center gap-1 hover:underline text-[#B93838]"
                              data-testid={`reports-drill-lost-${uid}`}
                            >
                              {expanded[lKey] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              <span className="font-semibold">{r.lost_count}</span>
                            </button>
                            <span className="text-[#8F9485] text-[10px] block">{fmtINR(r.lost_value)}</span>
                          </td>
                          <td className="py-2 px-3 text-right font-mono-data font-semibold">
                            {r.count > 0 ? `${((r.closed_count / r.count) * 100).toFixed(1)}%` : '—'}
                          </td>
                        </tr>
                        {['pending', 'lost'].map(status => {
                          const key = `${uid}:${status}`;
                          const drill = expanded[key];
                          if (!drill) return null;
                          return (
                            <tr key={key} className="bg-[#F9FAF7]" data-testid={`reports-drill-panel-${uid}-${status}`}>
                              <td colSpan={7} className="py-2 px-3">
                                {drill.loading ? (
                                  <div className="text-[11px] text-[#8F9485] flex items-center gap-1">
                                    <Loader2 className="w-3 h-3 animate-spin" /> Loading {status}…
                                  </div>
                                ) : drill.rows.length === 0 ? (
                                  <p className="text-[11px] text-[#8F9485] italic">No {status} quotations in range.</p>
                                ) : (
                                  <div className="border border-[#E2E6D9] rounded bg-white overflow-x-auto">
                                    <table className="w-full text-[11px] min-w-[560px]">
                                      <thead className="bg-[#F2F4EF] text-[#5C6150]">
                                        <tr>
                                          <th className="text-left py-1.5 px-2">Quote #</th>
                                          <th className="text-left py-1.5 px-2">Customer</th>
                                          <th className="text-left py-1.5 px-2">Created</th>
                                          <th className="text-right py-1.5 px-2">Days</th>
                                          <th className="text-right py-1.5 px-2">Value</th>
                                          {status === 'lost' && <th className="text-left py-1.5 px-2">Lost reason</th>}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {drill.rows.map(q => (
                                          <tr key={q.id} className="border-t border-[#E2E6D9]">
                                            <td className="py-1 px-2 font-mono-data text-[#556B2F]">{q.quotation_number || '—'}</td>
                                            <td className="py-1 px-2 truncate max-w-[180px]" title={q.customer_name}>{q.customer_name || '—'}</td>
                                            <td className="py-1 px-2">{q.created_at ? q.created_at.slice(0, 10) : '—'}</td>
                                            <td className="py-1 px-2 text-right">{q.days_since ?? '—'}</td>
                                            <td className="py-1 px-2 text-right font-mono-data text-[#DAA520]">{fmtINR(q.grand_total)}</td>
                                            {status === 'lost' && (
                                              <td className="py-1 px-2 italic text-[#8F9485]" title={q.lost_reason || ''}>
                                                {q.lost_reason || '—'}
                                              </td>
                                            )}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {summary && (
            <p className="text-[10px] text-[#8F9485] pt-1" data-testid="reports-summary-hint">
              Range totals — {summary.total_count} quotations, {fmtINR(summary.total_value)}. Numbers reuse the
              Analytics tab&apos;s dedupe + comparison-collapse pipeline; Conversion % = closed ÷ total.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
